import React from "react";
import { Tabs, Typography } from "antd";
import { useSearchParams } from "react-router-dom";

const { Title, Text } = Typography;

export interface TabHubItem {
  /** Value written to `?tab=`. Keep stable — these end up in bookmarks. */
  key: string;
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

interface TabHubProps {
  title: string;
  subtitle?: string;
  items: TabHubItem[];
  /** Falls back to the first tab when `?tab=` is absent or unrecognised. */
  defaultTab?: string;
  extra?: React.ReactNode;
}

/**
 * Tabbed hub page shell.
 *
 * The active tab lives in `?tab=`, mirroring the contract the personal settings
 * page has always used, so hub tabs are linkable and survive a refresh.
 *
 * `destroyInactiveTabPane` is deliberately on: Refine runs with
 * `syncWithLocation: true`, so every mounted `useTable` writes its pagination,
 * sorters and filters into the *same* query string. Two tables mounted at once
 * would overwrite each other's `current`/`pageSize`/`filters`. Unmounting the
 * inactive panes guarantees only one table syncs at a time.
 */
export const TabHub: React.FC<TabHubProps> = ({
  title,
  subtitle,
  items,
  defaultTab,
  extra,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const fallback = defaultTab ?? items[0]?.key;
  const requested = searchParams.get("tab");
  const activeKey = items.some((i) => i.key === requested)
    ? (requested as string)
    : fallback;

  const onChange = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    // Drop table state belonging to the tab we are leaving, so a stale
    // `current=7` from one table never lands on the next tab's table.
    ["current", "pageSize", "sorters", "filters"].forEach((p) => {
      next.delete(p);
      [...next.keys()]
        .filter((k) => k.startsWith(`${p}[`))
        .forEach((k) => next.delete(k));
    });
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: subtitle ? 4 : 0 }}>
          {title}
        </Title>
        {subtitle && <Text type="secondary">{subtitle}</Text>}
      </div>
      <Tabs
        activeKey={activeKey}
        onChange={onChange}
        destroyInactiveTabPane
        tabBarExtraContent={extra}
        items={items.map(({ key, label, icon, children }) => ({
          key,
          label,
          icon,
          children,
        }))}
      />
    </div>
  );
};
